/**
 * The lazy-loaded emoji picker itself, with no trigger of its own.
 *
 * Extracted from `emoji-picker-button.tsx` so a second trigger shape (the
 * message reaction bar, which is a row of quick emoji with a "more" button)
 * can mount the same picker without that component growing trigger/placement/
 * modal props for its six existing call sites.
 *
 * Same dynamic-import specifier as before, so there is still exactly one
 * ~20KB chunk and it still loads only when someone actually opens a picker.
 */
import { lazy, Suspense, useCallback } from 'react';
import { useTheme } from 'next-themes';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/ui/spinner';

import type { EmojiClickData, Theme } from 'emoji-picker-react';

const EmojiPicker = lazy(() => import('emoji-picker-react'));

interface EmojiPickerPanelProps {
    readonly onSelect: (emoji: string) => void;
}

export function EmojiPickerPanel({ onSelect }: EmojiPickerPanelProps) {
    const { t } = useTranslation();
    const { resolvedTheme } = useTheme();
    const pickerTheme: Theme = (resolvedTheme === 'dark' ? 'dark' : 'light') as Theme;

    const handleClick = useCallback(
        (data: EmojiClickData) => {
            onSelect(data.emoji);
        },
        [onSelect],
    );

    return (
        <Suspense
            fallback={
                <div className="flex h-80 w-72 items-center justify-center rounded-lg border border-border-default bg-bg-surface shadow-md">
                    <Spinner />
                </div>
            }
        >
            <EmojiPicker
                onEmojiClick={handleClick}
                theme={pickerTheme}
                lazyLoadEmojis
                searchPlaceholder={t('inbox.composer.emoji_search', 'Search emoji…')}
                previewConfig={{ showPreview: false }}
                width={320}
                height={400}
            />
        </Suspense>
    );
}
